import {
  IonButton, IonButtons, IonCard, IonCardContent, IonContent, IonFooter, IonHeader, IonIcon, IonInput, IonItem,
  IonLabel, IonList, IonLoading, IonModal, IonNote, IonPopover, IonSpinner, IonText, IonTitle,
  IonToast, IonToggle, IonToolbar
} from '@ionic/react';
import { arrowBack, informationCircleOutline } from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  apiMarketingBuscarConfiguracao, apiMarketingSalvarConfiguracao,
  apiMarketingTestarConexao, requestService
} from '../../../../../Utils/Services';

type Props = {
  isOpen: boolean;
  onDidDismiss: () => void;
  loja: string;
  onStatusChange?: (ativo: boolean) => void;
};

type FormState = {
  ativo: boolean;
  ga4MeasurementId: string;
  ga4ApiSecret: string;
  rastreamentoAutomatico: boolean;
  exigirConsentimento: boolean;
  debug: boolean;
};

type ValidacaoStatus = {
  meta?: { validado: boolean; dataUltimaValidacao: string };
  ga4?: { validado: boolean; dataUltimaValidacao: string };
};

type TesteResultado = {
  meta: { ok: boolean };
  ga4: { ok: boolean };
  dataValidacao: string;
} | null;

type InfoIntegracaoProps = {
  triggerId: string;
  children: React.ReactNode;
};

const InfoIntegracao: React.FC<InfoIntegracaoProps> = ({ triggerId, children }: InfoIntegracaoProps) => (
  <>
    <span id={triggerId} className="integracoes-info-trigger" role="button" tabIndex={0}>
      <IonIcon icon={informationCircleOutline} style={{ fontSize: 14 }} />
    </span>
    <IonPopover
      className="integracoes-popover-host"
      trigger={triggerId}
      showBackdrop={false}
      side="bottom"
      alignment="start"
    >
      <div className="integracoes-popover">
        {children}
      </div>
    </IonPopover>
  </>
);

const DEFAULT_STATE: FormState = {
  ativo: false,
  ga4MeasurementId: '',
  ga4ApiSecret: '',
  rastreamentoAutomatico: true,
  exigirConsentimento: true,
  debug: false,
};

function getValidacaoKey(loja: string) {
  return `marketing_validacao_${loja}`;
}

function formatarData(iso: string): string {
  try {
    return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

type IntegracaoStatusType = 'nao-configurada' | 'configurada' | 'validada';

function getStatusGa4(measurementId: string, validacao: ValidacaoStatus): IntegracaoStatusType {
  if (!measurementId) return 'nao-configurada';
  if (validacao.ga4?.validado) return 'validada';
  return 'configurada';
}

function StatusBadge({ status }: { status: IntegracaoStatusType }) {
  if (status === 'validada') return <span style={{ color: '#2dd36f', fontWeight: 600 }}>🟢 Validada</span>;
  if (status === 'configurada') return <span style={{ color: '#ffc409', fontWeight: 600 }}>🟡 Configurada</span>;
  return <span style={{ color: '#eb445a', fontWeight: 600 }}>🔴 Não configurada</span>;
}

const GA4_REGEX = /^G-[A-Z0-9]{6,15}$/i;

/**
 * Modal de configuração da integração Google Analytics 4.
 * Compartilha o mesmo endpoint backend com Meta Ads — cada modal envia
 * apenas seus próprios campos.
 */
const IntegracaoAnalyticsModal: React.FC<Props> = ({ isOpen, onDidDismiss, loja, onStatusChange }) => {
  const history = useHistory();

  const [state, setState] = useState<FormState>(DEFAULT_STATE);
  const [originalGa4SecretMasked, setOriginalGa4SecretMasked] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [testando, setTestando] = useState(false);
  const [testeResultado, setTesteResultado] = useState<TesteResultado>(null);
  const [validacao, setValidacao] = useState<ValidacaoStatus>({});
  const [toastText, setToastText] = useState('');
  const [showToast, setShowToast] = useState(false);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setState(prev => ({ ...prev, [k]: v }));

  useEffect(() => {
    if (!isOpen || !loja) return;
    const saved = localStorage.getItem(getValidacaoKey(loja));
    setValidacao(saved ? JSON.parse(saved) : {});
    setTesteResultado(null);
    setLoading(true);
    const url = apiMarketingBuscarConfiguracao.url.replace('{id}', loja.replaceAll('#', '%23'));
    requestService(
      url,
      { method: apiMarketingBuscarConfiguracao.method },
      history,
      (resp: any) => {
        setLoading(false);
        const item = (resp && resp.Item) || null;
        if (!item) {
          setState(DEFAULT_STATE);
          setOriginalGa4SecretMasked('');
          return;
        }
        setOriginalGa4SecretMasked(item.ga4ApiSecret || '');
        const ativo = item.analyticsAtivo === true || (item.analyticsAtivo === undefined && item.ativo === true);
        setState({
          ativo,
          ga4MeasurementId: item.ga4MeasurementId || '',
          ga4ApiSecret: item.ga4ApiSecret || '',
          rastreamentoAutomatico: item.rastreamentoAutomatico !== false,
          exigirConsentimento: item.exigirConsentimento !== false,
          debug: !!item.debug,
        });
      },
      (err: any) => {
        setLoading(false);
        console.log('[Analytics] erro buscar configuração', err);
      }
    );
  }, [isOpen, loja]);

  const validar = (): string | null => {
    if (state.ga4MeasurementId && !GA4_REGEX.test(state.ga4MeasurementId)) {
      return 'Measurement ID inválido (formato G-XXXXXXXXXX).';
    }
    if (state.ativo && !state.ga4MeasurementId) {
      return 'Informe o Measurement ID para ativar o Google Analytics.';
    }
    return null;
  };

  const salvar = () => {
    const erro = validar();
    if (erro) {
      setToastText(erro);
      setShowToast(true);
      return;
    }
    setLoading(true);
    const url = apiMarketingSalvarConfiguracao.url.replace('{id}', loja.replaceAll('#', '%23'));

    const body: any = {
      analyticsAtivo: state.ativo,
      ga4MeasurementId: state.ga4MeasurementId,
      rastreamentoAutomatico: state.rastreamentoAutomatico,
      exigirConsentimento: state.exigirConsentimento,
      debug: state.debug,
    };
    if (state.ga4ApiSecret !== originalGa4SecretMasked) body.ga4ApiSecret = state.ga4ApiSecret;

    requestService(
      url,
      {
        method: apiMarketingSalvarConfiguracao.method,
        body: JSON.stringify(body),
        headers: [['Content-Type', 'application/json']],
      },
      history,
      (_resp: any) => {
        setLoading(false);
        setToastText('Configurações do Google Analytics salvas.');
        setShowToast(true);
        if (onStatusChange) onStatusChange(state.ativo);
        onDidDismiss();
      },
      (err: any) => {
        setLoading(false);
        setToastText((err && err.message) || 'Erro ao salvar configurações.');
        setShowToast(true);
      }
    );
  };

  const executarTeste = () => {
    if (testando) return;
    setTestando(true);
    setTesteResultado(null);
    requestService(
      apiMarketingTestarConexao.url.replace('{id}', loja.replaceAll('#', '%23')),
      {
        method: apiMarketingTestarConexao.method,
        body: JSON.stringify({ empresaId: loja }),
        headers: [['Content-Type', 'application/json']],
      },
      history,
      (resp: any) => {
        setTestando(false);
        const dataValidacao = new Date().toISOString();
        const metaOk = resp.meta?.ok === true && (resp.meta?.response?.events_received ?? 0) > 0;
        let ga4Ok = false;

        if (resp.ga4?.ok === true) {
          const status = resp.ga4?.response?.status;

          if (status === 204) {
            ga4Ok = true;
          } else if (status === 200) {
            try {
              const body = JSON.parse(resp.ga4.response.body || '{}');

              ga4Ok = Array.isArray(body.validationMessages) &&
                body.validationMessages.length === 0;
            } catch {
              ga4Ok = false;
            }
          }
        }
        const resultado: TesteResultado = {
          meta: { ok: metaOk },
          ga4: { ok: ga4Ok },
          dataValidacao,
        };
        setTesteResultado(resultado);
        const key = getValidacaoKey(loja);
        const existing: ValidacaoStatus = JSON.parse(localStorage.getItem(key) || '{}');
        const updated: ValidacaoStatus = {
          meta: metaOk ? { validado: true, dataUltimaValidacao: dataValidacao } : existing.meta,
          ga4: ga4Ok ? { validado: true, dataUltimaValidacao: dataValidacao } : existing.ga4,
        };
        localStorage.setItem(key, JSON.stringify(updated));
        setValidacao(updated);
      },
      (err: any) => {
        setTestando(false);
        setTesteResultado({ meta: { ok: false }, ga4: { ok: false }, dataValidacao: new Date().toISOString() });
        setToastText((err && err.message) || 'Erro ao executar teste.');
        setShowToast(true);
      },
      false
    );
  };

  const statusGa4 = getStatusGa4(state.ga4MeasurementId, validacao);

  const desativar = () => {
    setLoading(true);
    const url = apiMarketingSalvarConfiguracao.url.replace('{id}', loja.replaceAll('#', '%23'));
    requestService(
      url,
      {
        method: apiMarketingSalvarConfiguracao.method,
        body: JSON.stringify({ analyticsAtivo: false }),
        headers: [['Content-Type', 'application/json']],
      },
      history,
      () => {
        setLoading(false);
        setToastText('Google Analytics desativado.');
        setShowToast(true);
        if (onStatusChange) onStatusChange(false);
        onDidDismiss();
      },
      (err: any) => {
        setLoading(false);
        setToastText((err && err.message) || 'Erro ao desativar.');
        setShowToast(true);
      }
    );
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onDidDismiss}>
      <IonHeader className="ion-no-border">
        <IonToolbar>
          <IonButtons slot="start" onClick={onDidDismiss}>
            <IonIcon slot="icon-only" icon={arrowBack} />
          </IonButtons>
          <IonTitle>Google Analytics</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding integracoes-modal-content">
        <IonCard className="integracoes-card">
          <IonCardContent>
            <div className="integracoes-section-title">
              <h2>Google Analytics</h2>
              <IonNote>Acompanhe acessos, pedidos e comportamento dos clientes em tempo real.</IonNote>
            </div>
          </IonCardContent>
        </IonCard>

        <IonCard className="integracoes-card">
          <IonCardContent>
            <div className="integracoes-section-title">
              <h2>Status</h2>
            </div>
            <IonList className="integracoes-list">
              <IonItem lines="full" className="integracoes-option">
                <IonLabel className="ion-text-wrap">Integração ativa</IonLabel>
                <IonToggle
                  slot="end"
                  checked={state.ativo}
                  onIonChange={(e) => set('ativo', e.detail.checked)}
                />
              </IonItem>
              <IonItem lines="none" className="integracoes-option">
                <IonLabel className="ion-text-wrap">
                  <IonNote>Status da integração</IonNote>
                  <div className="integracoes-status-badge"><StatusBadge status={statusGa4} /></div>
                  {validacao.ga4?.dataUltimaValidacao && (
                    <p>Última validação: {formatarData(validacao.ga4.dataUltimaValidacao)}</p>
                  )}
                </IonLabel>
              </IonItem>
            </IonList>
          </IonCardContent>
        </IonCard>

        <IonCard className="integracoes-card">
          <IonCardContent>
            <div className="integracoes-section-title">
              <h2>Credenciais GA4</h2>
            </div>
            <div className="integracoes-field">
              <div className="integracoes-field-label integracoes-title-row">
                <span>Measurement ID</span>
                <InfoIntegracao triggerId="info-ga4-measurement-id">
                  <p>Encontrado em Admin &gt; Streams no GA4.</p>
                </InfoIntegracao>
              </div>
              <IonInput
                placeholder="G-XXXXXXXXXX"
                value={state.ga4MeasurementId}
                onIonInput={(e: any) => set('ga4MeasurementId', (e.target.value || '').trim().toUpperCase())}
              />
            </div>
            <div className="integracoes-field">
              <div className="integracoes-field-label integracoes-title-row">
                <span>API Secret (opcional)</span>
                <InfoIntegracao triggerId="info-ga4-api-secret">
                  <p>Habilita envio server-side de eventos (precisão maior).</p>
                </InfoIntegracao>
              </div>
              <IonInput
                type="password"
                placeholder="API Secret do Measurement Protocol"
                value={state.ga4ApiSecret}
                onIonInput={(e: any) => set('ga4ApiSecret', e.target.value || '')}
              />
            </div>
          </IonCardContent>
        </IonCard>

        <IonCard className="integracoes-card">
          <IonCardContent>
            <div className="integracoes-section-title">
              <h2>Configurações</h2>
            </div>
            <IonList className="integracoes-list">
              <IonItem lines="full" className="integracoes-option">
                <IonLabel className="ion-text-wrap">Rastreamento automático de páginas</IonLabel>
                <IonToggle
                  slot="end"
                  checked={state.rastreamentoAutomatico}
                  onIonChange={(e) => set('rastreamentoAutomatico', e.detail.checked)}
                />
              </IonItem>
              <IonItem lines="full" className="integracoes-option">
                <IonLabel className="ion-text-wrap">Exigir consentimento LGPD</IonLabel>
                <IonToggle
                  slot="end"
                  checked={state.exigirConsentimento}
                  onIonChange={(e) => set('exigirConsentimento', e.detail.checked)}
                />
              </IonItem>
              <IonItem lines="none" className="integracoes-option">
                <IonLabel className="ion-text-wrap">Modo debug</IonLabel>
                <IonToggle
                  slot="end"
                  checked={state.debug}
                  onIonChange={(e) => set('debug', e.detail.checked)}
                />
              </IonItem>
            </IonList>
          </IonCardContent>
        </IonCard>

        {testeResultado && (
          <div style={{ marginTop: 16 }}>
            <IonText><h4 style={{ marginBottom: 8 }}>Resultado do teste</h4></IonText>
            <div style={{ background: 'var(--ion-color-light)', borderRadius: 8, padding: '12px 16px', marginBottom: 8 }}>
              <p style={{ margin: '0 0 4px', fontWeight: 600 }}>Google Analytics</p>
              {testeResultado.ga4.ok
                ? <p style={{ margin: 0, color: '#2dd36f' }}>✅ Integração validada — evento recebido pelo Google Analytics com sucesso.</p>
                : <p style={{ margin: 0, color: '#eb445a' }}>❌ Falha na integração — verifique Measurement ID e API Secret.</p>
              }
            </div>
            {/* <div style={{ background: 'var(--ion-color-light)', borderRadius: 8, padding: '12px 16px' }}>
              <p style={{ margin: '0 0 4px', fontWeight: 600 }}>Meta Ads</p>
              {testeResultado.meta.ok
                ? <p style={{ margin: 0, color: '#2dd36f' }}>✅ Integração validada — evento recebido pela Meta com sucesso.</p>
                : <p style={{ margin: 0, color: '#eb445a' }}>❌ Falha na integração — verifique Pixel ID e Token da API de Conversões.</p>
              }
            </div> */}
          </div>
        )}

        <IonLoading isOpen={loading} message="Aguarde..." />
        <IonToast
          isOpen={showToast}
          message={toastText}
          duration={3000}
          onDidDismiss={() => setShowToast(false)}
        />
      </IonContent>
      <IonFooter>
        <IonToolbar>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 8 }}>
            <IonButton
              expand="block"
              color="medium"
              fill="outline"
              onClick={executarTeste}
              disabled={testando || !state.ga4MeasurementId}
            >
              {testando && <IonSpinner name="crescent" style={{ marginRight: 8 }} />}
              {testando ? 'Testando...' : 'Executar teste de integração'}
            </IonButton>
            <IonButton expand="block" color="primary" onClick={salvar}>
              Salvar
            </IonButton>
          </div>
        </IonToolbar>
      </IonFooter>
    </IonModal>
  );
};

export default IntegracaoAnalyticsModal;
